import React, { useState } from 'react';
import { Menu, Bell, Settings, Plus, X, Home, ClipboardList, Brain, Briefcase, User, LogOut, Moon, Sun, Volume2, Mail, Heart, Calendar, Camera, Target, Globe, Plane, Users, ImageIcon, CheckSquare, Crown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { EventForm } from './Calendar/EventForm';
import { NoteEditor } from './BrainDump/NoteEditor';
import { TravelPlanForm } from './Travel/TravelPlanForm';
import { BottomNav } from './Navigation/BottomNav';
import { ProfileAvatar } from './Profile/ProfileAvatar';
import { useProfile } from '../contexts/ProfileContext';
import { QuickAdd } from './QuickAdd';
import { addTask } from '../lib/tasks';
import { addNote } from '../lib/notes';
import { addTravelPlan } from '../lib/travel';
import { useCalendar } from '../hooks/useCalendar';
import { CalendarSync } from './Calendar/CalendarSync';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { profile } = useProfile();
  const { addEvent } = useCalendar();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showBrainDump, setShowBrainDump] = useState(false);
  const [showTravelPlan, setShowTravelPlan] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showQuickAddMenu, setShowQuickAddMenu] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showCalendarSync, setShowCalendarSync] = useState(false);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleQuickAdd = () => {
    switch (location.pathname) {
      case '/business':
        setShowQuickAdd(true);
        break;
      case '/brain-dump':
        setShowBrainDump(true);
        break;
      case '/travel':
        setShowTravelPlan(true);
        break;
      case '/lists':
        setShowList(true);
        break;
      case '/calendar':
        setShowQuickAddMenu(true);
        break;
      default:
        setShowQuickAdd(true);
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear all auth state before signing out
      localStorage.clear();
      sessionStorage.clear();
      
      // Sign out from Supabase
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      // The onAuthStateChange listener will handle the redirect
    }
  };

  React.useEffect(() => {
    if (isMobileMenuOpen || isMenuOpen || showSettings) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [isMobileMenuOpen, isMenuOpen, showSettings]);

  return (
    <div className="min-h-full bg-black flex flex-col">
      {/* Desktop Header */}
      <div className="hidden md:flex items-center justify-between px-8 py-4 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-2xl font-bold text-white">07:AM</Link>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              onClick={() => setIsDesktopMenuOpen(!isDesktopMenuOpen)}
              className="p-2 hover:bg-black/20 rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            {isDesktopMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-black rounded-xl shadow-lg py-2 z-50">
                <Link
                  to="/"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <Home className="w-4 h-4" />
                  Home
                </Link>
                <Link
                  to="/calendar"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <Calendar className="w-4 h-4" />
                  Calendar
                </Link>
                <Link
                  to="/tasks"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <CheckSquare className="w-4 h-4" />
                  Task Board
                </Link>
                <Link
                  to="/brain-dump"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <Brain className="w-4 h-4" />
                  Brain Dump
                </Link>
                <Link
                  to="/lists"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <ClipboardList className="w-4 h-4" />
                  Lists
                </Link>
                <div className="border-t border-gray-800 my-2"></div>
                <Link
                  to="/business"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <Briefcase className="w-4 h-4" />
                  Business
                </Link>
                <Link
                  to="/travel"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <Plane className="w-4 h-4" />
                  Travel
                </Link>
                <Link
                  to="/challenges"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <Target className="w-4 h-4" />
                  Challenges
                </Link>
                <Link
                  to="/mood-board"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <ImageIcon className="w-4 h-4" />
                  Mood Board
                </Link>
                <div className="border-t border-gray-800 my-2"></div>
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-4 py-2 text-base text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  <User className="w-4 h-4" />
                  Profile
                </Link>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-base text-red-500 hover:bg-gray-800 w-full text-left"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-[100] bg-gray-900/50 md:hidden transition-opacity duration-300 ${
        isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        <div className={`fixed inset-y-0 right-0 w-72 bg-black shadow-lg flex flex-col overflow-y-auto transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="p-4 flex justify-end">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 hover:bg-black/20 rounded-lg text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex-1 p-4 flex flex-col justify-center space-y-1">
            <h2 className="text-[1.375rem] font-bold text-white mb-4">ACTIONS</h2>
            <Link
              to="/"
              className={`px-3 py-1.5 rounded-lg ${
                location.pathname === '/' ? 'bg-[var(--accent-color)] text-black' : 'text-gray-300 hover:bg-black/20'
              }`}
            >
              <span className="text-base">Home</span>
            </Link>
            <Link
              to="/calendar"
              className={`px-3 py-1.5 rounded-lg ${
                location.pathname === '/calendar' ? 'bg-[var(--accent-color)] text-black' : 'text-gray-300 hover:bg-black/20'
              }`}
            >
              <span className="text-base">Calendar</span>
            </Link>
            <Link
              to="/mood-board"
              className={`px-3 py-1.5 rounded-lg ${
                location.pathname === '/mood-board' ? 'bg-[var(--accent-color)] text-black' : 'text-gray-300 hover:bg-black/20'
              }`}
            >
              <span className="text-base">Mood Board</span>
            </Link>
            <Link
              to="/business"
              className={`px-3 py-1.5 rounded-lg ${
                location.pathname === '/business' ? 'bg-[var(--accent-color)] text-black' : 'text-gray-300 hover:bg-black/20'
              }`}
            >
              <span className="text-base">Business</span>
            </Link>
            <Link
              to="/profile"
              className={`px-3 py-1.5 rounded-lg ${
                location.pathname === '/profile' ? 'bg-[var(--accent-color)] text-black' : 'text-gray-300 hover:bg-black/20'
              }`}
            >
              <span className="text-base">Account</span>
            </Link>
            <div className="border-t border-gray-800 my-2"></div>
            <Link
              to="/subscription"
              className={`p-1 flex flex-col items-center ${
                location.pathname === '/subscription' ? 'text-[var(--accent-color)]' : 'text-white'
              }`}
            >
              <Crown className="w-6 h-6" />
            </Link>
            <button
              onClick={handleSignOut}
              className="px-3 py-1.5 rounded-lg text-red-500 hover:bg-black/20"
            >
              <span className="text-base">Sign Out</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Desktop Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 bg-gray-900/50">
          <div className="fixed inset-y-0 right-0 w-72 bg-black shadow-lg flex flex-col">
            <div className="p-4 flex justify-between items-center">
              <h2 className="font-semibold text-[var(--accent-color)]">Menu</h2>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 hover:bg-black/20 rounded-lg text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-2">
              <Link
                to="/"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <span>Home</span>
              </Link>
              <Link
                to="/tasks"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/tasks' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <span>Task Board</span>
              </Link>
              <Link
                to="/brain-dump"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/brain-dump' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <span>Brain Dump</span>
              </Link>
              <Link
                to="/lists"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/lists' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <ClipboardList className="w-5 h-5 text-white" />
                <span>Lists</span>
              </Link>
              <Link
                to="/mood-board"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/mood-board' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <span>Mood Board</span>
              </Link>
              <Link
                to="/calendar"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/calendar' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <span>Calendar</span>
              </Link>
              <Link
                to="/business"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/business' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <span>Business</span>
              </Link>
              <Link
                to="/travel"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/travel' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <span>Travel</span>
              </Link>
              <Link
                to="/profile"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/profile' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <User className="w-5 h-5" />
                <span>Account</span>
              </Link>
              <Link
                to="/subscription"
                className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                  location.pathname === '/subscription' ? 'bg-[var(--accent-color)] text-black' : 'hover:bg-black/20'
                }`}
              >
                <Crown className="w-5 h-5" />
                <span>Upgrade to Premium</span>
              </Link>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 p-3 rounded-lg text-red-500 hover:bg-black/20"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20 md:pb-8 overflow-y-auto scrollbar-hide">
        {children}
        {/* Desktop Quick Add Button (only on homepage and business) */}
        {(location.pathname === '/' || location.pathname === '/business') && (
          <button
            onClick={handleQuickAdd}
            className="hidden md:flex fixed bottom-8 right-8 w-14 h-14 bg-[var(--accent-color)] rounded-full shadow-lg items-center justify-center hover:opacity-90 transition-opacity z-50"
          >
            <Plus className="w-6 h-6 text-black" />
          </button>
        )}
      </main>
      
      {/* Bottom Navigation */}
      <div className="md:hidden">
        <BottomNav currentPath={location.pathname} onOpenMenu={handleMobileMenuToggle} />
        {/* Mobile Quick Add Button */}
        <button
          onClick={handleQuickAdd}
          className="fixed bottom-24 right-6 md:bottom-8 w-14 h-14 bg-[var(--accent-color)] rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity z-50"
        >
          <Plus className="w-6 h-6 text-black" />
        </button>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Quick Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-black/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-black/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5" />
                  <span>Dark Mode</span>
                </div>
                <button className="w-11 h-6 bg-[var(--accent-color)] rounded-full relative">
                  <span className="absolute right-1 top-1 w-4 h-4 bg-black rounded-full" />
                </button>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-black/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5" />
                  <span>Sound Effects</span>
                </div>
                <button className="w-11 h-6 bg-[var(--accent-color)] rounded-full relative">
                  <span className="absolute right-1 top-1 w-4 h-4 bg-black rounded-full" />
                </button>
              </div>
              
              <Link
                to="/profile"
                onClick={() => setShowSettings(false)}
                className="flex items-center justify-between p-4 bg-black/20 rounded-xl hover:bg-black/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5" />
                  <span>All Settings</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Add Modal */}
      {showQuickAdd && (
        <QuickAdd
          onAdd={async (task) => {
            try {
              await addTask(task);
              setShowQuickAdd(false);
            } catch (error) {
              console.error('Error adding task:', error);
            }
          }}
          onClose={() => setShowQuickAdd(false)}
        />
      )}
      
      {/* Brain Dump Quick Add */}
      {showBrainDump && (
        <NoteEditor
          folders={[]}
          onSave={async (note) => {
            try {
              await addNote(note);
              setShowBrainDump(false);
            } catch (error) {
              console.error('Error adding note:', error);
            }
          }}
          onClose={() => setShowBrainDump(false)}
        />
      )}
      
      {/* Travel Plan Quick Add */}
      {showTravelPlan && (
        <TravelPlanForm
          onSubmit={async (plan) => {
            try {
              await addTravelPlan(plan);
              setShowTravelPlan(false);
            } catch (error) {
              console.error('Error adding travel plan:', error);
            }
          }}
          onClose={() => setShowTravelPlan(false)}
        />
      )}
      
      {/* Calendar Sync Modal */}
      {showCalendarSync && (
        <CalendarSync onClose={() => setShowCalendarSync(false)} />
      )}
      
      {/* Calendar Quick Add Menu */}
      {showQuickAddMenu && (
        <div 
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowQuickAddMenu(false);
            }
          }}
        >
          <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-xs w-full mx-4">
            <div className="space-y-2">
              <button
                onClick={() => {
                  setShowEventForm(true);
                  setShowQuickAddMenu(false);
                }}
                className="w-full flex items-center gap-3 p-3 bg-black/20 rounded-xl hover:bg-black/30 transition-colors text-left"
              >
                <Plus className="w-5 h-5" />
                <span>New Event</span>
              </button>
              <button
                onClick={() => {
                  setShowCalendarSync(true);
                  setShowQuickAddMenu(false);
                }}
                className="w-full flex items-center gap-3 p-3 bg-black/20 rounded-xl hover:bg-black/30 transition-colors text-left"
              >
                <Calendar className="w-5 h-5" />
                <span>Sync Calendar</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Event Form */}
      {showEventForm && (
        <EventForm
          onSubmit={async (eventData) => {
            try {
              const startTime = new Date(eventData.startTime);
              const endTime = new Date(eventData.endTime);
              
              await addEvent({
                ...eventData,
                startTime,
                endTime,
              });
              setShowEventForm(false);
            } catch (error) {
              console.error('Error adding event:', error);
            }
          }}
          onClose={() => setShowEventForm(false)}
        />
      )}
      
      {/* List Quick Add */}
      {showList && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Create New List</h3>
              <button
                onClick={() => setShowList(false)}
                className="p-2 hover:bg-black/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const name = formData.get('name') as string;
              if (name) {
                // Add new list logic here
                setShowList(false);
              }
            }}>
              <input
                type="text"
                name="name"
                placeholder="List name"
                className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
                required
              />
              <button
                type="submit"
                className="w-full mt-4 bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
              >
                Create List
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}